<script>
  import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  /**
   * Array representing the table columns.
   * @type {Array}
   */
  export let columns;

  /**
   * Array representing the table rows.
   * @type {Array}
   */
  export let rows;

  /**
   * Sorting key (optional 2-way binding).
   * @type {string}
   * @default ''
   */
  export let sortBy = '';

  /**
   * The sorting order (1 for ascending, -1 for descending).
   * @type {number}
   * @default 1
   */
  export let sortOrder = 1;

  /**
   * Object storing filter selections for each column.
   * @type {Object}
   * @default {}
   */
  export let filterSelections = {};

  /**
   * Flag indicating whether column filters are displayed.
   * @type {boolean}
   * @default false
   */
  export let showColumnFilters = false;

  /**
   * Flag indicating whether rows should expand on click.
   * @type {boolean}
   * @default false
   */
  export let expandOnRowClick = false;

  /**
   * Array of expanded row indices.
   * @type {Array}
   * @default []
   */
  export let expanded = [];

  /**
   * Identifier for the table.
   * @type {string}
   * @default null
   */
  export let id = null;

  /**
   * Key for expanding a specific row.
   * @type {string}
   * @default null
   */
  export let expandRowKey = null;

  /**
   * Flag indicating single-row expansion.
   * @type {boolean}
   * @default false
   */
  export let expandSingle = false;

  /**
   * Flag indicating the visibility of the expand all icon.
   * @type {boolean}
   * @default false
   */
  export let showExpandAll = false;

  /**
   * Flag indicating the visibility of the expand icon.
   * @type {boolean}
   * @default false
   */
  export let showExpandIcon = false;

  /**
   * Custom class name for the table.
   * @type {string}
   * @default ''
   */
  export let classNameTable = '';

  /**
   * Custom class name for the table header.
   * @type {string}
   * @default ''
   */
  export let classNameThead = '';

  /**
   * Custom class name for the table body.
   * @type {string}
   * @default ''
   */
  export let classNameTbody = '';

  /**
   * Custom class name for the table select element.
   * @type {string}
   * @default ''
   */
  export let classNameSelect = '';

  /**
   * Custom class name for table rows.
   * @type {string}
   * @default ''
   */
  export let classNameRow = '';

  /**
   * Custom class name for table cells.
   * @type {string}
   * @default ''
   */
  export let classNameCell = '';

  /**
   * Custom class name for expanded rows.
   * @type {string}
   * @default ''
   */
  export let classNameRowExpanded = '';

  /**
   * Custom class name for expanded content.
   * @type {string}
   * @default ''
   */
  export let classNameExpandedContent = '';

  /**
   * Custom class name for expandable cells.
   * @type {string}
   * @default ''
   */
  export let classNameCellExpand = '';

  /**
   * The display text when the table is empty
   * @type {string}
   * @default 'label.NO_DATA_AVAILABLE'
   */
  export let emptyLabel = 'label.NO_DATA_AVAILABLE';

  /**
   * Returns extra class names for a given row. Use for per-row state that
   * `classNameRow` cannot express, such as selection highlighting.
   * @type {(row: Object) => string}
   * @default () => ''
   */
  export let rowClass = () => '';

  const dispatch = createEventDispatcher();

  export let onSortChange = () => {};

  let sortFunction = () => '';

  if (!Array.isArray(expanded)) throw "'expanded' needs to be an array";

  let hasColumnFilters = columns.some((c) => {
    return c.filterOptions !== undefined || c.searchValue !== undefined;
  });

  if (expandOnRowClick) {
    classNameRow += ' isClickable';
  }

  let filterValues = {};
  let columnByKey;

  const asStringArray = (v) =>
    []
      .concat(v)
      .filter((v) => typeof v === 'string' && v !== '')
      .join(' ');

  const calculateFilterValues = () => {
    filterValues = {};
    columns.forEach((c) => {
      if (typeof c.filterOptions === 'function') {
        filterValues[c.key] = c.filterOptions(rows);
      } else if (Array.isArray(c.filterOptions)) {
        // if array of strings is provided, use it for name and value
        filterValues[c.key] = c.filterOptions.map((val) => ({
          name: val,
          value: val
        }));
      }
    });
  };

  const updateSortOrder = (colKey) => {
    if (colKey === sortBy) {
      sortOrder = -sortOrder;
    }

    onSortChange(sortBy, sortOrder);
  };

  const handleClickCol = (event, col) => {
    if (col.sortable) {
      updateSortOrder(col.key);
      sortBy = col.key;
    }
    dispatch('clickCol', { event, col, key: col.key });
  };

  const handleClickRow = (event, row) => {
    dispatch('clickRow', { event, row });
  };

  const handleClickExpand = (event, row) => {
    row.$expanded = !row.$expanded;
    const keyVal = row[expandRowKey];
    if (expandSingle && row.$expanded) {
      expanded = [keyVal];
    } else if (expandSingle) {
      expanded = [];
    } else if (!row.$expanded) {
      // eslint-disable-next-line
      expanded = expanded.filter((r) => r != keyVal);
    } else {
      expanded = [...expanded, keyVal];
    }

    dispatch('clickExpand', { event, row });
  };

  const handleClickExpandAll = () => {
    if (expanded.length === rows.length) {
      expanded = []; // Collapse all
    } else {
      expanded = rows.map((row) => row[expandRowKey]); // Expand all
    }
  };

  const handleClickCell = (event, row, key) => {
    if (expandOnRowClick) {
      handleClickExpand(event, row);
    }

    dispatch('clickCell', { event, row, key });
  };

  $: {
    columnByKey = {};

    columns.forEach((col) => {
      columnByKey[col.key] = col;
    });
  }

  $: colspan = (showExpandIcon ? 1 : 0) + columns.length;
  $: c_rows = rows
    .filter((r) => {
      // get search and filter results/matches
      return Object.keys(filterSelections).every((f) => {
        // check search (text input) matches
        let resSearch =
          filterSelections[f] === '' ||
          (columnByKey[f].searchValue &&
            (columnByKey[f].searchValue(r) + '')
              .toLocaleLowerCase()
              .indexOf((filterSelections[f] + '').toLocaleLowerCase()) >= 0);
        // check filter (dropdown) matches
        let resFilter =
          resSearch ||
          filterSelections[f] === undefined ||
          // default to value() if filterValue() not provided in col
          filterSelections[f] ===
            (typeof columnByKey[f].filterValue === 'function'
              ? columnByKey[f].filterValue(r)
              : columnByKey[f].value(r));
        return resFilter;
      });
    })
    .map((r) =>
      Object.assign({}, r, {
        // internal row property for sort order
        $sortOn: sortFunction(r),
        // internal row property for expanded rows
        $expanded: expandRowKey !== null && expanded.indexOf(r[expandRowKey]) >= 0
      })
    )
    .sort((a, b) => {
      if (a.$sortOn > b.$sortOn) {
        return sortOrder;
      } else if (a.$sortOn < b.$sortOn) {
        return -sortOrder;
      }

      return 0;
    });
  $: expandAll = expanded.length < rows.length;

  const toggleFilters = () => {
    showColumnFilters = !showColumnFilters;
  };
  $: {
    let col = columnByKey[sortBy];

    if (col !== undefined && col.sortable === true && typeof col.value === 'function') {
      sortFunction = (r) => col.value(r);
    }
  }

  $: {
    // if filters are enabled, watch rows and columns
    if (hasColumnFilters && columns && rows) {
      calculateFilterValues();
    }
  }
</script>

<table {id} class={asStringArray(classNameTable)}>
  <thead class={asStringArray(classNameThead)}>
    <slot name="header" {sortOrder} {sortBy}>
      <tr>
        {#if showExpandIcon}
          {#if showExpandAll}
            <th class="expanded-icon isClickable" on:click={handleClickExpandAll}>
              <Icon name={expandAll ? 'chevron-down' : 'chevron-up'} />
            </th>
          {:else}
            <th class="expanded-icon" />
          {/if}
        {/if}
        {#each columns as col}
          <th
            on:click={(e) => handleClickCol(e, col)}
            class={asStringArray([col.sortable ? 'isSortable' : '', col.columnClass, col.headerClass])}
          >
            <div class="column-title">
              <span class="column-title-text">
                {#if col.title?.component}
                  <svelte:component this={col.title.component} bind:props={col.title.props} {col} />
                {:else}
                  {@html col.title}
                {/if}

                {#if col.tooltip}
                  <Icon name="help-circle" id="column-tooltip-{col.key}" />
                  <Tooltip target="column-tooltip-{col.key}" placement="top">
                    {@html col.tooltip}
                  </Tooltip>
                {/if}
              </span>
              {#if sortBy === col.key}
                <Icon name={sortOrder === 1 ? 'arrow-up' : 'arrow-down'} />
              {/if}
            </div>
          </th>
        {/each}
        {#if hasColumnFilters}
          <th class="filters-action">
            <button class:active={showColumnFilters} on:click={toggleFilters}>
              <Icon name="search" />
            </button>
          </th>
        {/if}
      </tr>
    </slot>

    {#if hasColumnFilters && showColumnFilters}
      <tr class="filters">
        {#if showExpandIcon}
          <th class="expanded-icon" />
        {/if}
        {#each columns as col}
          <th class={asStringArray([col.columnClass, classNameCell])}>
            {#if col.searchValue !== undefined}
              <input type="text" bind:value={filterSelections[col.key]} placeholder={col.searchPlaceholder} />
            {:else if filterValues[col.key] !== undefined}
              <select bind:value={filterSelections[col.key]} class={asStringArray(classNameSelect)}>
                <option value={undefined} />
                {#each filterValues[col.key] as option}
                  <option value={option.value}>{option.name}</option>
                {/each}
              </select>
            {/if}
          </th>
        {/each}
        {#if hasColumnFilters}
          <th />
        {/if}
      </tr>
    {/if}
  </thead>

  <tbody class={asStringArray(classNameTbody)}>
    {#if !c_rows.length}
      <tr>
        <td colspan="20" class="empty">{$format(emptyLabel)}</td>
      </tr>
    {:else}
      {#each c_rows as row, n}
        <slot name="row" {row} {n}>
          <tr
            on:click={(e) => handleClickRow(e, row)}
            class={asStringArray([classNameRow, row.$expanded && classNameRowExpanded, rowClass(row)])}
          >
            {#if showExpandIcon}
              <td
                on:click={(e) => handleClickExpand(e, row)}
                class={asStringArray(['isClickable', 'expanded-icon', classNameCellExpand])}
              >
                <Icon name={row.$expanded ? 'chevron-up' : 'chevron-down'} />
              </td>
            {/if}

            {#each columns as col}
              <td
                on:click={(e) => {
                  handleClickCell(e, row, col.key);
                }}
                class={asStringArray([col.columnClass, col.cellClass, classNameCell])}
              >
                {#if col.renderComponent}
                  <svelte:component
                    this={col.renderComponent.component || col.renderComponent}
                    bind:props={col.renderComponent.props}
                    {row}
                  />
                {:else}
                  {@html col.renderValue ? col.renderValue(row) : col.value(row)}
                {/if}
              </td>
            {/each}
            {#if hasColumnFilters}
              <td>&nbsp;</td>
            {/if}
          </tr>
          {#if row.$expanded}
            <tr class={asStringArray(classNameExpandedContent)}>
              <td {colspan}>
                <slot name="expanded" {row} {n} />
              </td>
            </tr>
          {/if}
        </slot>
      {/each}
    {/if}
  </tbody>
</table>

<style lang="scss" src="./table.scss"></style>
